import type { JSX } from "react";
import { truncateText } from "@brains/utils/string-utils";
import type { PaginationInfo } from "@brains/plugins";
import {
  Head,
  Pagination,
  StatusBadge,
  Card,
  formatDate,
} from "@brains/ui-library";
import type { SocialPostView } from "./social-post-view";

export interface SocialPostListProps {
  posts: SocialPostView[];
  pageTitle?: string | undefined;
  pagination?: PaginationInfo | null | undefined;
  baseUrl: string | null;
}

export const SocialPostListTemplate = ({
  posts,
  pageTitle,
  pagination,
  baseUrl,
}: SocialPostListProps): JSX.Element => {
  const title = pageTitle ?? "Social Posts";
  const totalCount = pagination?.totalItems ?? posts.length;
  const description = `Browse all ${totalCount} social ${totalCount === 1 ? "post" : "posts"}`;

  return (
    <>
      <Head title={title} description={description} />
      <div className="social-post-list bg-theme">
        <div className="container mx-auto px-6 md:px-12 max-w-4xl py-16 md:py-24">
          <h1 className="text-3xl md:text-4xl font-bold text-heading mb-8">
            {title}
          </h1>

          {posts.length === 0 ? (
            <p className="text-theme-muted italic">No social posts yet.</p>
          ) : (
            <ul className="space-y-6">
              {posts.map((post) => (
                <li key={post.id}>
                  <Card href={post.url} variant="horizontal">
                    <div className="flex flex-col sm:flex-row gap-4">
                      {post.coverImageUrl && (
                        <img
                          src={post.coverImageUrl}
                          alt={post.frontmatter.title}
                          className="w-full sm:w-24 h-48 sm:h-24 object-cover rounded-lg shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4 mb-2">
                          <h2 className="text-lg font-semibold text-heading">
                            {post.frontmatter.title}
                          </h2>
                          <time className="text-sm text-theme-muted shrink-0">
                            {formatDate(
                              post.frontmatter.publishedAt ?? post.created,
                              { style: "medium" },
                            )}
                          </time>
                        </div>
                        <div className="flex items-center gap-2 mb-3">
                          <StatusBadge status={post.frontmatter.status} />
                          <span className="text-xs text-theme-muted uppercase">
                            {post.frontmatter.platform}
                          </span>
                          <span className="text-xs text-theme-muted font-mono">
                            {post.id}
                          </span>
                        </div>
                        <p className="text-theme leading-relaxed">
                          {truncateText(post.body, 200)}
                        </p>
                      </div>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          )}

          {pagination && pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              baseUrl={baseUrl ?? "/social-posts"}
            />
          )}
        </div>
      </div>
    </>
  );
};
